// components/WhatYouLearn.jsx
import React, { useState, useRef, useEffect } from 'react';
import './WhatYouLearn.css';
import EligibilityModal from './EligibilityModal';

const WhatYouLearn = ({ scrollToCalendar, openEligibilityModal }) => {
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [logos, setLogos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSubtitle, setSectionSubtitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const carouselRef = useRef(null);
  const positionRef = useRef(0);
  const animationFrameIdRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchConfianceData();
  }, []);

  const fetchConfianceData = async () => {
    try {
      setLoading(true);
      // const response = await fetch('http://localhost/ivtcms/api/rubriques/by-url/Ils-nous-font-confiance');
      const response = await fetch(`${process.env.REACT_APP_HOTE}api/rubriques/by-url/Ils-nous-font-confiance`,)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        const rubriqueData = data.data;
        
        // 1. Récupérer les informations de la rubrique
        setSectionTitle(rubriqueData.titre || "");
        setSectionSubtitle(rubriqueData.descriptif || "");
        
        // 2. Récupérer les documents (logos) depuis l'API
        if (rubriqueData.documents && rubriqueData.documents.length > 0) {
          const formattedLogos = rubriqueData.documents.map((doc) => ({
            id: doc.id_document,
            name: doc.titre,
            image: doc.url, // URL directe depuis l'API
            description: doc.descriptif || ""
          }));
          setLogos(formattedLogos);
        } else {
          setLogos([]); // Pas de fallback
        }
        
        // 3. Récupérer les articles (témoignages) depuis l'API
        if (rubriqueData.articles && rubriqueData.articles.length > 0) {
          const formattedTestimonials = rubriqueData.articles.map((article) => ({
            id: article.id_article,
            name: article.titre,
            certification: article.soustitre || "",
            text: cleanText(article.texte),
            avatar: getInitials(article.titre)
          }));
          setTestimonials(formattedTestimonials);
        } else {
          setTestimonials([]); // Pas de fallback
        }
      } else {
        throw new Error('Données de rubrique non disponibles');
      }
      
    } catch (err) {
      console.error('Erreur lors de la récupération des données:', err);
      setError(err.message);
      // Pas de données par défaut en cas d'erreur
      setLogos([]);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  // Nettoyer le texte des témoignages
  const cleanText = (text) => {
    if (!text) return "";
    return text
      .replace(/^"|"$/g, '')
      .replace(/\r\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Obtenir les initiales du nom
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleEligibilityComplete = (results) => {
    console.log('Résultats du questionnaire:', results);
    setShowEligibilityModal(false);
    
    if (results.recommendation.certification === 'PMP') {
      scrollToCalendar();
    }
  };

  // Animation du carrousel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || logos.length === 0) return;

    const speed = 1.0;

    const animate = () => {
      if (!carousel || isPaused || logos.length === 0) return;
      
      positionRef.current -= speed;
      
      // Réinitialiser la position quand on arrive à la fin du premier ensemble
      const firstChild = carousel.firstElementChild;
      if (firstChild && positionRef.current <= -firstChild.offsetWidth * logos.length) {
        positionRef.current = 0;
        // Réorganiser les éléments pour l'effet de boucle continu
        for (let i = 0; i < logos.length; i++) {
          carousel.appendChild(carousel.firstElementChild);
        }
      }
      
      carousel.style.transform = `translateX(${positionRef.current}px)`;
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    // Démarrer ou arrêter l'animation selon l'état
    if (!isPaused && logos.length > 0) {
      animationFrameIdRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [isPaused, logos]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Dupliquer les logos pour l'effet de boucle infini
  const allLogos = logos.length > 0 ? [...logos, ...logos, ...logos] : [];

  if (loading) {
    return (
      <section className="section what-you-learn" id="programme" >
        <div className="contenir">
          <h2 className="section-title">Références</h2>
          <p className="section-subtitle">
            Chargement des données...
          </p>
          
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section what-you-learn" id="programme" >
        <div className="contenir">
          <h2 className="section-title">Références</h2>
          <p className="section-subtitle" style={{color: '#ff6b6b'}}>
            Erreur de chargement : {error}
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="section what-you-learn" id="programme" >
        <div className="contenir">
          <h2 className="section-title">{sectionTitle}</h2>
          {sectionSubtitle && (
            <p className="section-subtitle">
              {sectionSubtitle}
            </p>
          )}

          {/* CARROUSEL DES LOGOS D'ENTREPRISES */}
          {logos.length > 0 ? (
            <div 
              className="logos-carousel-container"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div 
                ref={carouselRef}
                className="logos-carousel-track"
                style={{
                  display: 'flex',
                  width: 'max-content',
                  alignItems: 'center'
                }}
              >
                {allLogos.map((logo, index) => (
                  <div 
                    key={`${logo.id}-${index}`}
                    className="logo-card"
                    style={{
                      flex: '0 0 auto',
                      width: '250px',
                      height: '180px',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      margin: '0 1.5rem',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Conteneur pour l'image */}
                    <div style={{
                      width: '100%',
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1rem'
                    }}>
                      <img 
                        src={logo.image} 
                        alt={logo.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          opacity: 0.85,
                          transition: 'all 0.3s ease'
                        }}
                        onError={(e) => {
                          console.error(`Erreur de chargement de l'image: ${logo.name}`, logo.image);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    {/* Effet de survol */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      bottom: '0',
                      backgroundColor: 'rgba(10, 55, 100, 0)',
                      transition: 'background-color 0.3s ease',
                      pointerEvents: 'none'
                    }}></div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', margin: '4rem 0' }}>
              Aucun partenaire à afficher pour le moment.
            </p>
          )}

          {/* TÉMOIGNAGES */}
          {/* {testimonials.length > 0 ? (
            <div className="testimonials-container">
              <h3 className="testimonials-title">Ils témoignent</h3>
              <div className="testimonials-grid">
                {testimonials.map((testimonial, index) => (
                  <div className="testimonial-card" key={testimonial.id || index}>
                    <p className="testimonial-content">"{testimonial.text}"</p>
                    <div className="testimonial-author">
                      <div className="author-avatar">
                        <div className="avatar-circle">{testimonial.avatar}</div>
                      </div>
                      <div className="author-info">
                        <h4 className="author-name">{testimonial.name}</h4>
                        {testimonial.certification && (
                          <p className="author-certification">{testimonial.certification}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', margin: '4rem 0' }}>
              Aucun témoignage à afficher pour le moment.
            </p>
          )} */}
        </div>
      </section>

      <style jsx>{`
        .loading-placeholder {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 4px;
        }
        .logos-carousel-track {
          
          margin-top: 40px;
        }

        
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </>
  );
};

export default WhatYouLearn;